import { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { aiApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Send, Bot, User, Plus, Trash2, ArrowRightLeft, X, Minus,
  CheckCircle2, AlertCircle, Loader2, Cpu, UserPlus, RefreshCw,
  CircleDot, Sparkles, ListTodo, MessageSquare
} from 'lucide-react';

const QUICK_CMDS = [
  { text: 'Create a task "Follow up with client" for tomorrow', icon: Plus },
  { text: 'Show all pending high priority tasks', icon: ListTodo },
  { text: 'Mark task "Site Survey" as completed', icon: CheckCircle2 },
];

const INTENT_COLORS = {
  create_task: '#22c55e', assign_task: '#3b82f6', delete_task: '#ef4444',
  transfer_task: '#f59e0b', update_task: '#8b5cf6', complete_task: '#10b981', list_tasks: '#06b6d4',
};
const INTENT_ICONS = {
  create_task: Plus, assign_task: UserPlus, delete_task: Trash2,
  transfer_task: ArrowRightLeft, update_task: RefreshCw, complete_task: CheckCircle2, list_tasks: ListTodo,
};

function ProcessingDots() {
  return (
    <div className="flex items-center gap-3 px-4 py-3">
      <div className="relative w-8 h-8 flex items-center justify-center flex-shrink-0">
        <motion.div className="absolute inset-0 rounded-md border border-cyan-500/40" animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: 'linear' }} />
        <Cpu className="w-3.5 h-3.5 text-cyan-400" />
      </div>
      <div>
        <div className="flex items-center gap-1.5">
          <motion.span className="text-xs font-mono text-cyan-400" animate={{ opacity: [1, 0.4, 1] }} transition={{ duration: 1.2, repeat: Infinity }}>PROCESSING</motion.span>
          {[0, 1, 2].map(i => (
            <motion.div key={i} className="w-1 h-1 rounded-full bg-cyan-400" animate={{ opacity: [0.2, 1, 0.2] }} transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.2 }} />
          ))}
        </div>
        <div className="flex gap-0.5 mt-1">
          {['PARSE', 'MATCH', 'EXEC'].map((s, i) => (
            <motion.div key={s} className="h-0.5 rounded-full bg-cyan-500/30 w-8 overflow-hidden">
              <motion.div className="h-full bg-cyan-400 rounded-full" initial={{ width: '0%' }} animate={{ width: '100%' }} transition={{ duration: 1, delay: i * 0.5 }} />
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ActionChip({ action }) {
  const type = action.type || '';
  const color = type.includes('created') ? '#22c55e' : type.includes('deleted') ? '#ef4444' : type.includes('assigned') || type.includes('transferred') ? '#3b82f6' : type.includes('completed') ? '#10b981' : '#8b5cf6';
  const label = type.includes('created') ? `Created: ${action.task?.title || ''}` : type.includes('deleted') && !type.includes('bulk') ? `Deleted: ${action.title || ''}` : type.includes('bulk_deleted') ? `Bulk deleted ${action.count} tasks` : type.includes('assigned') || type.includes('transferred') ? `${action.title || ''} -> ${action.new_owner || ''}` : type.includes('completed') ? 'Completed' : type;

  return (
    <div className="flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-mono" style={{ borderColor: `${color}30`, backgroundColor: `${color}08`, color }}>
      <CheckCircle2 className="w-3 h-3 flex-shrink-0" />
      <span className="truncate">{label}</span>
    </div>
  );
}

export function AIChatbotWidget() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [hasNewMsg, setHasNewMsg] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (messages.length === 0) {
      setMessages([{
        role: 'bot',
        message: `Hi ${user?.name?.split(' ')[0] || 'there'}! I'm your AI task assistant. Tell me what you need — create, assign, delete, or manage tasks using plain English.`,
      }]);
    }
  }, [user?.name]);

  useEffect(() => {
    if (scrollRef.current) {
      const el = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (el) setTimeout(() => { el.scrollTop = el.scrollHeight; }, 50);
    }
  }, [messages, loading]);

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  const handleSend = async (text) => {
    const command = (text || input).trim();
    if (!command || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: command }]);
    setLoading(true);

    try {
      const res = await aiApi.taskCommand(command);
      const d = res.data;
      setMessages(prev => [...prev, {
        role: 'bot',
        message: d.message || 'Done.',
        intent: d.intent,
        actions: d.actions_taken || [],
        error: d.success === false ? d.message : null,
      }]);
      if (d.success && d.actions_taken?.length) toast.success(`${d.actions_taken.length} action(s) executed`);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'bot', message: 'Command failed.', error: err.response?.data?.detail || 'Error processing command',
      }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const toggleOpen = () => {
    setOpen(!open);
    setMinimized(false);
    setHasNewMsg(false);
  };

  if (!user) return null;

  return (
    <>
      {/* Floating Bubble */}
      <AnimatePresence>
        {!open && (
          <motion.button
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0 }}
            whileHover={{ scale: 1.08 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleOpen}
            className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#002FA7] text-white shadow-lg shadow-[#002FA7]/30 flex items-center justify-center"
            data-testid="ai-chatbot-bubble"
          >
            <Bot className="w-6 h-6" />
            {hasNewMsg && (
              <motion.div className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 rounded-full border-2 border-white" animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 1, repeat: Infinity }} />
            )}
            {/* Pulse ring */}
            <motion.div
              className="absolute inset-0 rounded-full border-2 border-[#002FA7]"
              animate={{ scale: [1, 1.5], opacity: [0.5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </motion.button>
        )}
      </AnimatePresence>

      {/* Chat Window */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className={`fixed z-50 bg-[#0d1117] rounded-2xl shadow-2xl shadow-black/30 border border-white/10 overflow-hidden flex flex-col ${minimized ? 'bottom-6 right-6 w-72 h-14' : 'bottom-6 right-6 w-[380px] h-[520px] sm:w-[400px] sm:h-[560px]'}`}
            data-testid="ai-chatbot-window"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 h-14 bg-[#161b22] border-b border-white/10 flex-shrink-0 cursor-pointer" onClick={() => minimized && setMinimized(false)}>
              <div className="flex items-center gap-2.5">
                <div className="relative">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500/20 to-[#002FA7]/20 border border-cyan-500/30 flex items-center justify-center">
                    <Bot className="w-4 h-4 text-cyan-400" />
                  </div>
                  <motion.div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-green-500 border border-[#161b22]" animate={{ scale: [1, 1.3, 1] }} transition={{ duration: 2, repeat: Infinity }} />
                </div>
                <div>
                  <p className="text-sm font-semibold text-white">AI Assistant</p>
                  {!minimized && <p className="text-[10px] text-cyan-400/70 font-mono">NLP Engine Active</p>}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={(e) => { e.stopPropagation(); setMinimized(!minimized); }} className="w-7 h-7 rounded-md hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors">
                  <Minus className="w-3.5 h-3.5" />
                </button>
                <button onClick={(e) => { e.stopPropagation(); setOpen(false); }} className="w-7 h-7 rounded-md hover:bg-white/10 flex items-center justify-center text-gray-400 hover:text-white transition-colors" data-testid="ai-chatbot-close">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>

            {/* Body */}
            {!minimized && (
              <>
                <ScrollArea className="flex-1 min-h-0" ref={scrollRef}>
                  <div className="p-4 space-y-4">
                    {messages.map((msg, i) => (
                      <div key={i}>
                        {msg.role === 'user' ? (
                          <div className="flex justify-end">
                            <div className="max-w-[85%] bg-[#002FA7]/25 border border-[#002FA7]/30 rounded-xl rounded-tr-sm px-3.5 py-2">
                              <p className="text-sm text-white/90">{msg.content}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2.5 items-start">
                            <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 border" style={{
                              borderColor: msg.intent ? `${INTENT_COLORS[msg.intent] || '#94a3b8'}40` : 'rgba(6,182,212,0.3)',
                              backgroundColor: msg.intent ? `${INTENT_COLORS[msg.intent] || '#94a3b8'}15` : 'rgba(6,182,212,0.1)',
                            }}>
                              {(() => {
                                const Icon = msg.intent ? (INTENT_ICONS[msg.intent] || CircleDot) : Bot;
                                const color = msg.intent ? (INTENT_COLORS[msg.intent] || '#94a3b8') : '#06b6d4';
                                return <Icon className="w-3.5 h-3.5" style={{ color }} />;
                              })()}
                            </div>
                            <div className="flex-1 min-w-0 space-y-1.5">
                              {msg.intent && (
                                <Badge variant="outline" className="text-[10px] font-mono border-0 px-1.5 py-0" style={{ color: INTENT_COLORS[msg.intent], backgroundColor: `${INTENT_COLORS[msg.intent]}15` }}>
                                  {msg.intent.replace('_', ' ').toUpperCase()}
                                </Badge>
                              )}
                              <p className="text-sm text-white/85 leading-relaxed">{msg.message}</p>
                              {msg.actions?.map((a, j) => <ActionChip key={j} action={a} />)}
                              {msg.error && (
                                <div className="flex items-center gap-1.5 text-red-400 text-xs font-mono bg-red-500/10 border border-red-500/20 rounded-md px-2.5 py-1.5">
                                  <AlertCircle className="w-3 h-3 flex-shrink-0" />{msg.error}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {loading && <ProcessingDots />}

                    {/* Quick commands on first load */}
                    {messages.length <= 1 && !loading && (
                      <div className="pt-2 space-y-1.5">
                        <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">Try these</p>
                        {QUICK_CMDS.map((cmd, i) => (
                          <button
                            key={i}
                            onClick={() => handleSend(cmd.text)}
                            className="flex items-center gap-2 w-full px-3 py-2 rounded-lg border border-white/8 bg-white/[0.02] hover:bg-white/[0.05] hover:border-cyan-500/20 text-left transition-all group"
                            data-testid={`quick-cmd-${i}`}
                          >
                            <cmd.icon className="w-3.5 h-3.5 text-cyan-500/50 group-hover:text-cyan-400 flex-shrink-0" />
                            <span className="text-xs text-gray-400 group-hover:text-gray-300 truncate">{cmd.text}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </ScrollArea>

                {/* Input */}
                <div className="p-3 bg-[#161b22] border-t border-white/10 flex-shrink-0">
                  <div className="flex items-center gap-2 bg-[#0d1117] border border-white/10 rounded-xl px-3 py-2 focus-within:border-cyan-500/40 transition-colors">
                    <Sparkles className="w-3.5 h-3.5 text-cyan-500/40 flex-shrink-0" />
                    <input
                      ref={inputRef}
                      type="text"
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder='e.g. "Create a task for Rahul"'
                      className="flex-1 bg-transparent border-0 outline-none text-sm text-white/90 placeholder:text-gray-600 font-mono"
                      disabled={loading}
                      data-testid="ai-chatbot-input"
                    />
                    <Button
                      size="sm"
                      onClick={() => handleSend()}
                      disabled={!input.trim() || loading}
                      className="h-7 w-7 p-0 rounded-lg bg-cyan-600 hover:bg-cyan-500 disabled:opacity-30"
                      data-testid="ai-chatbot-send"
                    >
                      {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
